import React, { useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Input, Select, Modal, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, IconButton } from '@datum-components/react-components';
import { Controller, useFieldArray } from 'react-hook-form';

import { UB04_FIELDS } from 'utils/ub04';
import { DATE_FORMAT } from 'utils/hcfaConstants';

// --- your existing helpers (kept as-is) ---
import { toCurrency, toCurrencyPaste } from 'helpers/toCurrency';
import { toPositiveNumber } from 'helpers/toPositiveNumber';
import { toPositiveFloat } from 'helpers/toPositiveFloat';
import { toAlphaNumUppercase } from 'helpers/toAlphaNumUppercase';
import { handleRevenueCode } from 'helpers/handleRevenueCode';
import { emptyService, FormContext } from '../../common/FormProvider';
import TableTotals from './TableTotalsRHF';
import DatePickerRHF from '../../common/DatePickerRHF';
import { put42ServicesInGlobalObject } from '../../../utils/hcfa/putFormProvidersDataInGlobalObject';
import { ValidationTableDependenciesMap } from '../../../utils/ub04/customRules';
import { makeWarnFromYup } from '../../../utils/ub04/validataions';
import PowerController from './ControllerWithWarning';

// If you want totals, keep using your TableTotals or compute locally via useWatch (shown below).
// import TableTotals from './TableTotals';

// ---- TYPES ----
interface Props {
  readOnly: boolean;
}

export type PaperServicesForm = {
  ['42Services']: any[];
  ['49ub_total_charges_sum']?: string | number;
};

const PER_PAGE = 20;
const NDC_QUALIFIER = ['F2', 'GR', 'ME', 'ML', 'UN'];

function PaperServicesListRHF({ readOnly }: Props) {
  const { t } = useTranslation();

  const { methodsUb, form } = useContext(FormContext);

  const tableTotalsRef = useRef<any>(null);

  const {
    control,
    register,
    setValue,
    formState: { errors },
    setFocus,
    watch,
    getValues,
    reset
  } = methodsUb;

  const { fields, append, remove } = useFieldArray({
    control,
    name: '42Services'
  });

  // const values = watch();

  // console.log('values ', values);

  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRow = useRef<number>();

  const pagedFields = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    const end = start + PER_PAGE;

    const pagedFields = fields.map((r, i) => ({ ...r, _idx: i })).slice(start, end);
    return pagedFields;
  }, [fields, currentPage]);

  const onAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const prevCount = fields.length;
    append(emptyService, { shouldFocus: false });
    if (prevCount % PER_PAGE === 0) setCurrentPage(Math.floor(prevCount / PER_PAGE) + 1);

    requestAnimationFrame(() => setFocus(`42Services.${prevCount}.${UB04_FIELDS.rev_cd_42}` as const));
  };

  const triggerDependantFieldsInTable = (fieldName: string, idx: number) => {
    if (ValidationTableDependenciesMap[fieldName]) {
      ValidationTableDependenciesMap[fieldName].forEach((fName) => {
        methodsUb.trigger(`42Services.${idx}.${fName}`);
      });
    }
  };

  const showModal = (open: boolean) => setIsModalOpen(open);

  const err = (gi: number, name: keyof any) => (errors?.['42Services'] as any)?.[gi]?.[name];
  const help = (gi: number, name: keyof any) => (err(gi, name)?.message as string) || undefined;
  const status = (gi: number, name: keyof any) => (err(gi, name) ? 'error' : undefined) as any;

  const columns: ColumnsType<any> = [
    // Hidden field_43_description
    {
      title: '',
      key: UB04_FIELDS.field_43_description,
      render: (_: any, record: any) => (
        <input
          type="hidden"
          {...register(`42Services.${record._idx}.${UB04_FIELDS.field_43_description}` as const)}
          onInput={(e: any) => (e.target.value = e.target.value.toUpperCase())}
        />
      )
    },
    {
      title: 'S.No',
      key: 'sno',
      fixed: 'left',
      render: (_: any, record: any) => {
        return record._idx + 1;
      }
    },
    {
      title: '42. REV CODE',
      key: 'rev',
      width: '5%',
      fixed: 'left',
      render: (_: any, record: any) => (
        <div className="ant-form-item CODE" data-status={status(record._idx, UB04_FIELDS.rev_cd_42)}>
          <div className="ant-form-item-control">
            {/* <label className="ant-form-item-no-colon">Revenue Code</label> */}
            <Controller
              name={`42Services.${record._idx}.${UB04_FIELDS.rev_cd_42}`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id={`${UB04_FIELDS.rev_cd_42}__${record._idx}`}
                  style={{ width: 70 }}
                  disabled={readOnly}
                  // value={field.value ?? ''}
                  onChange={(e) => {
                    toPositiveNumber(e);
                    handleRevenueCode(e);
                    field.onChange(e.target.value);
                    const tableValues = methodsUb.getValues('42Services');
                    put42ServicesInGlobalObject(tableValues);
                    triggerDependantFieldsInTable(UB04_FIELDS.rev_cd_42, record._idx);
                  }}
                />
              )}
            />
            {/* <Input
              {...register(`42Services.${index}.${UB04_FIELDS.rev_cd_42}` as const)}
              onInput={(e: any) => {
                toPositiveNumber(e);
                handleRevenueCode(e);
              }}
              id={`${UB04_FIELDS.rev_cd_42}__${index}`}
              style={{ width: 70 }}
              disabled={readOnly}
            /> */}
            {help(record._idx, UB04_FIELDS.rev_cd_42) && (
              <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.rev_cd_42)}</div>
            )}
          </div>
        </div>
      )
    },
    {
      title: '43. NDC Code',
      key: 'ndc_code',
      fixed: 'left',
      render: (_: any, record: any) => (
        <div
          className="vertical_align_with_space ant-form-item"
          data-status={status(record._idx, UB04_FIELDS.desc_ndc_code)}
        >
          <Tooltip title="Code">
            <Controller
              name={`42Services.${record._idx}.${UB04_FIELDS.desc_ndc_code}`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  // value={field.value ?? ''}
                  placeholder="12345678901"
                  style={{ width: 70 }}
                  disabled={readOnly}
                  onChange={(e) => {
                    field.onChange(e.target.value.toUpperCase());
                    const tableValues = methodsUb.getValues('42Services');
                    put42ServicesInGlobalObject(tableValues);
                    triggerDependantFieldsInTable(UB04_FIELDS.desc_ndc_code, record._idx);
                  }}
                />
              )}
            />
          </Tooltip>
          {help(record._idx, UB04_FIELDS.desc_ndc_code) && (
            <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.desc_ndc_code)}</div>
          )}
        </div>
      )
    },
    {
      title: '43. NDC Qual',
      key: 'ndc_qual',
      render: (_: any, record: any) => (
        <div
          className="vertical_align_with_space ant-form-item"
          data-status={status(record._idx, UB04_FIELDS.desc_ndc_qty)}
        >
          <Tooltip title="Quantity">
            <Controller
              control={control}
              name={`42Services.${record._idx}.${UB04_FIELDS.desc_ndc_qty}` as const}
              render={({ field }) => (
                <Select
                  {...field}
                  allowClear
                  showSearch
                  options={NDC_QUALIFIER.map((x) => ({ label: x, value: x }))}
                  style={{ width: 65 }}
                  disabled={readOnly}
                />
              )}
            />
          </Tooltip>
          {help(record._idx, UB04_FIELDS.desc_ndc_qty) && (
            <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.desc_ndc_qty)}</div>
          )}
        </div>
      )
    },
    {
      title: '43. NDC Units',
      key: 'ndc_units',
      width: '5%',
      render: (_: any, record: any) => (
        <div
          className="vertical_align_with_space ant-form-item"
          data-status={status(record._idx, UB04_FIELDS.desc_ndc_unit)}
        >
          <Tooltip title="Units">
            <Controller
              name={`42Services.${record._idx}.${UB04_FIELDS.desc_ndc_unit}`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="123"
                  style={{ width: 70 }}
                  disabled={readOnly}
                  onChange={(e) => {
                    field.onChange(e.target.value.toUpperCase());
                    const tableValues = methodsUb.getValues('42Services');
                    put42ServicesInGlobalObject(tableValues);
                    triggerDependantFieldsInTable(UB04_FIELDS.desc_ndc_unit, record._idx);
                  }}
                />
              )}
            />
            {/* <Input
              {...register(`42Services.${index}.${UB04_FIELDS.desc_ndc_unit}` as const)}
              placeholder="123"
              style={{ width: 70 }}
              onInput={(e: any) => (e.target.value = e.target.value.toUpperCase())}
              disabled={readOnly}
            /> */}
          </Tooltip>
          {help(record._idx, UB04_FIELDS.desc_ndc_unit) && (
            <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.desc_ndc_unit)}</div>
          )}
        </div>
      )
    },
    {
      title: '44. HCPCS Description',
      key: 'hcpcs_desc',
      width: '5%',
      render: (_: any, record: any) => (
        <div
          className="vertical_align_with_space ant-form-item"
          data-status={status(record._idx, UB04_FIELDS.hcpcs_description_44)}
        >
          <Tooltip title="HCPCS Description">
            {/* <label className="ant-form-item-no-colon">HCPCS Description</label> */}
            <Controller
              name={`42Services.${record._idx}.${UB04_FIELDS.hcpcs_description_44}`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  disabled={readOnly}
                  onChange={(e) => {
                    field.onChange(e.target.value.toUpperCase());
                    const tableValues = methodsUb.getValues('42Services');
                    put42ServicesInGlobalObject(tableValues);
                    triggerDependantFieldsInTable(UB04_FIELDS.hcpcs_description_44, record._idx);
                  }}
                />
              )}
            />
          </Tooltip>
          {help(record._idx, UB04_FIELDS.hcpcs_description_44) && (
            <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.hcpcs_description_44)}</div>
          )}
        </div>
      )
    },
    {
      title: '44. HCPCS',
      key: 'hcpcs',
      width: '5%',
      render: (_: any, record: any) => (
        <div
          className="vertical_align_with_space ant-form-item"
          data-status={status(record._idx, UB04_FIELDS.hcpcs_rates_44)}
        >
          <Tooltip title="HCPCS">
            <Controller
              name={`42Services.${record._idx}.${UB04_FIELDS.hcpcs_rates_44}`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  style={{ width: 70 }}
                  disabled={readOnly}
                  onChange={(e) => {
                    toAlphaNumUppercase(e);
                    field.onChange(e.target.value);
                    const tableValues = methodsUb.getValues('42Services');
                    put42ServicesInGlobalObject(tableValues);
                    triggerDependantFieldsInTable(UB04_FIELDS.hcpcs_rates_44, record._idx);
                  }}
                />
              )}
            />
          </Tooltip>
          {help(record._idx, UB04_FIELDS.hcpcs_rates_44) && (
            <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.hcpcs_rates_44)}</div>
          )}
        </div>
      )
    },
    {
      title: 'Modifiers',
      key: 'modifiers',
      width: '15%',
      render: (_: any, record: any) => (
        <div className="flex">
          {[UB04_FIELDS.modifier_1, UB04_FIELDS.modifier_2, UB04_FIELDS.modifier_3, UB04_FIELDS.modifier_4].map(
            (mKey, i) => (
              <div key={mKey} style={{ marginRight: i < 3 ? 4 : 0 }}>
                <Controller
                  name={`42Services.${record._idx}.${mKey}`}
                  control={control}
                  render={({ field }) => {
                    return (
                      <Input
                        style={{ width: 45 }}
                        placeholder={`M${i + 1}`}
                        {...field}
                        onChange={(e) => {
                          field.onChange(e.target.value.toUpperCase());
                          const tableValues = methodsUb.getValues('42Services');
                          put42ServicesInGlobalObject(tableValues);
                          triggerDependantFieldsInTable(mKey, record._idx);
                        }}
                        disabled={readOnly}
                      />
                    );
                  }}
                />

                {help(record._idx, mKey as any) && (
                  <div className="ant-form-item-explain-error">{help(record._idx, mKey as any)}</div>
                )}
              </div>
            )
          )}
        </div>
      )
    },
    {
      title: '45. SERVICE DATE',
      key: 'dos',
      width: '10%',
      render: (_: any, record: any) => (
        <div className="ant-form-item mb-0" data-status={status(record._idx, UB04_FIELDS.dos_45)}>
          {/* <label className="ant-form-item-no-colon">SERVICE DATE</label> */}
          <PowerController
            control={control}
            name={`42Services.${record._idx}.${UB04_FIELDS.dos_45}` as const}
            render={({ field, warning }) => (
              <>
                <DatePickerRHF
                  {...field}
                  // value={field.value ? moment?.(field.value, DATE_FORMAT) : undefined}
                  format={DATE_FORMAT}
                  onChange={(dateString) => {
                    field.onChange(dateString);
                    const tableValues = methodsUb.getValues('42Services');
                    put42ServicesInGlobalObject(tableValues);
                    form?.validateFields([UB04_FIELDS.coversPeriodFrom, UB04_FIELDS.coversPeriodTo]);
                    triggerDependantFieldsInTable(UB04_FIELDS.dos_45, record._idx);
                  }}
                  placeholder={t('common:fields.dateFormat')}
                  disabled={readOnly}
                  style={{ width: '100%' }}
                  name={UB04_FIELDS.dos_45}
                  idx={record._idx}
                  suffixIcon={null}
                  panelRender={() => false}
                />
                {Boolean(warning) && <div className="ant-form-item-explain-warning">{warning}</div>}
              </>
            )}
            warn={makeWarnFromYup(t, UB04_FIELDS.dos_45, `42Services.${record._idx}.${UB04_FIELDS.dos_45}`)}
          />
          {help(record._idx, UB04_FIELDS.dos_45) && (
            <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.dos_45)}</div>
          )}
        </div>
      )
    },
    {
      title: '46. SERVICE UNITS',
      key: 'units',
      width: '8%',
      render: (_: any, record: any) => (
        <div className="ant-form-item mb-0" data-status={status(record._idx, UB04_FIELDS.serv_units_46)}>
          {/* <label className="ant-form-item-no-colon">SERVICE UNITS</label> */}
          <Controller
            name={`42Services.${record._idx}.${UB04_FIELDS.serv_units_46}`}
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="1234567890"
                style={{ minWidth: 70 }}
                disabled={readOnly}
                onChange={(e) => {
                  toPositiveFloat(e);
                  field.onChange(e.target.value);
                  const tableValues = methodsUb.getValues('42Services');
                  put42ServicesInGlobalObject(tableValues);
                  triggerDependantFieldsInTable(UB04_FIELDS.serv_units_46, record._idx);
                }}
              />
            )}
          />

          {help(record._idx, UB04_FIELDS.serv_units_46) && (
            <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.serv_units_46)}</div>
          )}
        </div>
      )
    },
    {
      title: '47. TOTAL CHARGES',
      key: 'total_charges',
      width: '8%',
      render: (_: any, record: any) => (
        <div className="ant-form-item mb-0" data-status={status(record._idx, UB04_FIELDS.total_charges_47)}>
          {/* <label className="ant-form-item-no-colon">TOTAL CHARGES</label> */}
          <Controller
            control={control}
            name={`42Services.${record._idx}.${UB04_FIELDS.total_charges_47}` as const}
            render={({ field }) => (
              <Input
                {...field}
                prefix="$"
                allowClear
                onPaste={toCurrencyPaste}
                onInput={toCurrency}
                style={{ minWidth: 60 }}
                disabled={readOnly}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  const tableValues = methodsUb.getValues('42Services');
                  put42ServicesInGlobalObject(tableValues);
                  tableTotalsRef.current?.handleTotalfeesCalculation();
                  triggerDependantFieldsInTable(UB04_FIELDS.total_charges_47, record._idx);
                }}
              />
            )}
          />
          {help(record._idx, UB04_FIELDS.total_charges_47) && (
            <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.total_charges_47)}</div>
          )}
        </div>
      )
    },
    {
      title: '48. NON-COVERED CHARGES',
      key: 'noncov',
      width: '8%',
      render: (_: any, record: any) => (
        <div className="ant-form-item mb-0" data-status={status(record._idx, UB04_FIELDS.nonCoveredCharges_48)}>
          {/* <label className="ant-form-item-no-colon">NON-COVERED CHARGES</label> */}
          <PowerController
            control={control}
            name={`42Services.${record._idx}.${UB04_FIELDS.nonCoveredCharges_48}` as const}
            render={({ field, warning }) => (
              <>
                <Input
                  {...field}
                  prefix="$"
                  allowClear
                  onPaste={toCurrencyPaste}
                  onInput={toCurrency}
                  style={{ minWidth: 60 }}
                  disabled={readOnly}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    const tableValues = methodsUb.getValues('42Services');
                    put42ServicesInGlobalObject(tableValues);
                    console.log('triggering ');
                    tableTotalsRef.current?.handleTotalfeesCalculation();
                    triggerDependantFieldsInTable(UB04_FIELDS.nonCoveredCharges_48, record._idx);
                  }}
                />
                {Boolean(warning) && <div className="ant-form-item-explain-warning">{warning}</div>}
              </>
            )}
            warn={makeWarnFromYup(
              t,
              UB04_FIELDS.nonCoveredCharges_48,
              `42Services.${record._idx}.${UB04_FIELDS.nonCoveredCharges_48}`
            )}
          />
          {help(record._idx, UB04_FIELDS.nonCoveredCharges_48) && (
            <div className="ant-form-item-explain-error">{help(record._idx, UB04_FIELDS.nonCoveredCharges_48)}</div>
          )}
        </div>
      )
    },
    {
      title: '49. SUM',
      key: 'ub49',
      width: '8%',
      render: (_: any, record: any) => {
        return (
          <div className="ant-form-item mb-0" data-status={status(record._idx, UB04_FIELDS.ub49)}>
            {/* <label className="ant-form-item-no-colon">TOTAL CHARGES</label> */}
            {/* <Input prefix="$" readOnly style={{ minWidth: 60 }} /> */}
            <Controller
              control={control}
              name={`42Services.${record._idx}.${UB04_FIELDS.ub49}` as const}
              render={({ field }) => (
                <Input
                  readOnly
                  {...field}
                  // onChange={(e) => {
                  //   field.onChange(e.target.value);
                  //   const tableValues = methodsUb.getValues('42Services');
                  //   put42ServicesInGlobalObject(tableValues);
                  //   triggerDependantFieldsInTable(UB04_FIELDS.ub49, record._idx);
                  // }}
                  prefix="$"
                  style={{ minWidth: 60 }}
                />
              )}
            />
          </div>
        );
      }
    },

    {
      title: '',
      key: 'actions',
      fixed: 'right',
      render: (_: any, record: any) => (
        <>
          <Modal
            style={{ top: -20 }}
            open={isModalOpen}
            onOk={() => {
              if (typeof modalRow.current === 'number') remove(modalRow.current);
              showModal(false);
            }}
            onCancel={() => showModal(false)}
            centered
            closable={false}
          >
            <div>
              <InfoCircleOutlined style={{ color: '#faad14' }} /> {' ' + t('hcfa:form.confirmMessage.deleteService')}
            </div>
          </Modal>
          <IconButton
            className="icon-btn icon-btn--primary-outline icon-btn--md about-button"
            onClick={() => {
              modalRow.current = record._idx;
              showModal(true);
            }}
            variant="outline"
            icon={<DeleteOutlined />}
            aria-label={t('delete')}
            tooltip={t('delete')}
            type="button"
            disabled={readOnly}
          />
        </>
      )
    }
  ];

  return (
    <div>
      <Table
        dataSource={pagedFields}
        // dataSource={fields}
        rowKey={(r) => {
          return r.id;
        }}
        // @ts-expect-error
        columns={columns.map((col) => ({ ...col, title: <span className="form_label_color">{col.title}</span> }))}
        pagination={{
          defaultPageSize: PER_PAGE,
          current: currentPage,
          onChange: (pg) => setCurrentPage(pg),
          total: fields.length,
          showSizeChanger: false
        }}
        bordered
        scroll={{ x: true }}
      />

      <TableTotals fields={fields} tableTotalsRef={tableTotalsRef} />

      <Button
        type="button"
        onMouseDown={onAdd}
        variant="primary"
        style={{ margin: '10px 5px 5px 5px' }}
        leftIcon={<PlusOutlined />}
        disabled={readOnly}
        onKeyDown={(e: any) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            const nextInput = document.getElementById('basic_50a_payer');
            if (nextInput) nextInput.focus();
          }
        }}
      >
        {t('hcfa:form.addService')}
      </Button>

      <input type="hidden" {...register('49ub_total_charges_sum')} />
    </div>
  );
}

export default PaperServicesListRHF;
